<template>
  <div>
    <div
      class="d-flex justify-content-between align-items-center mb-3 flex-wrap"
    >
      <a-button type="primary" icon="plus" @click="openModal(null)">
        {{ $t("action.ajouter") }}
      </a-button>
    </div>
    <a-table
      row-key="_id"
      :loading="loading"
      :columns="columns"
      :data-source="rows"
      :pagination="{ pageSize: 10 }"
      :scroll="{ x: 'max-content' }"
    >
      <template slot="typeName" slot-scope="text, record">
        <template v-if="getTypeDisplay(record)">
          <div>{{ getTypeDisplay(record).name || "—" }}</div>
          <a-tag
            v-if="getTypeDisplay(record).expenseKind"
            :color="kindColor(getTypeDisplay(record).expenseKind)"
          >
            {{ kindLabel(getTypeDisplay(record).expenseKind) }}
          </a-tag>
        </template>
        <span v-else>—</span>
      </template>
      <template slot="accountingClass" slot-scope="text">
        <a-tag v-if="text" color="geekblue">{{ describeClass(text) }}</a-tag>
        <span v-else class="text-muted">—</span>
      </template>
      <template slot="action" slot-scope="text, record">
        <div class="editable-row-operations">
          <a-button type="primary" size="small" @click="openModal(record)">
            {{ $t("action.modifier") }}
          </a-button>
          <a-button type="danger" size="small" class="ml-2">
            <a-popconfirm
              :title="$t('comptabilite.categoryDeactivateConfirm')"
              @confirm="() => remove(record._id)"
            >
              <a>{{ $t("action.supprimer") }}</a>
            </a-popconfirm>
          </a-button>
        </div>
      </template>
    </a-table>

    <a-modal
      :visible="modalVisible"
      :title="editingId ? $t('action.modifier') : $t('action.ajouter')"
      :confirm-loading="saving"
      :width="640"
      @ok="onModalOk"
      @cancel="closeModal"
      destroy-on-close
      wrap-class-name="parametrage-compact-modal"
    >
      <a-form :form="form" layout="vertical" class="compact-param-form">
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('etablissement.nom')">
              <a-input
                v-decorator="[
                  'name',
                  { rules: [{ required: true, message: $t('requis.nom') }] },
                ]"
              />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.typeDepense')">
              <a-select
                v-decorator="[
                  'depenseType',
                  {
                    rules: [{ required: true, message: $t('requis.depense') }],
                  },
                ]"
                show-search
                option-filter-prop="children"
              >
                <a-select-option v-for="t in types" :key="t._id" :value="t._id">
                  {{ t.name }} ({{ kindLabel(t.expenseKind) }})
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <a-form-item :label="$t('comptabilite.accountingClassReference')">
              <plan-comptable-select v-decorator="['accountingClass']" />
              <div class="text-muted small">
                {{ $t("comptabilite.accountingClassHint") }}
              </div>
              <div
                v-if="editingId && hasSubsWithClass"
                class="text-warning small"
              >
                {{ $t("comptabilite.accountingClassChangeBlocked") }}
              </div>
            </a-form-item>
          </div>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";
import { describePlanComptableCode } from "@/utilities/depense/planComptable";
import PlanComptableSelect from "@/views/compV3/decaissement/components/PlanComptableSelect.vue";

export default {
  name: "CategoriesTab",
  components: { PlanComptableSelect },
  beforeMount() {
    this.form = this.$form.createForm(this);
  },
  data() {
    return {
      loading: false,
      rows: [],
      types: [],
      subcategories: [],
      modalVisible: false,
      saving: false,
      editingId: null,
      hasSubsWithClass: false,
    };
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t("etablissement.nom"),
          dataIndex: "name",
          key: "name",
        },
        {
          title: this.$t("comptabilite.typeDepense"),
          key: "typeName",
          scopedSlots: { customRender: "typeName" },
        },
        {
          title: this.$t("comptabilite.accountingClass"),
          dataIndex: "accountingClass",
          key: "accountingClass",
          scopedSlots: { customRender: "accountingClass" },
        },
        {
          title: this.$t("action.actions"),
          key: "action",
          fixed: "right",
          scopedSlots: { customRender: "action" },
        },
      ];
    },
  },
  created() {
    this.loadTypes();
    this.loadSubcategories();
    this.load();
  },
  methods: {
    describeClass(code) {
      return describePlanComptableCode(code);
    },
    getTypeDisplay(record) {
      const dt = record && record.depenseType;
      if (!dt) return null;
      if (typeof dt === "object") {
        return {
          name: dt.name,
          expenseKind: dt.expenseKind || "OPEX",
        };
      }
      const t = (this.types || []).find((x) => String(x._id) === String(dt));
      if (!t) return null;
      return {
        name: t.name,
        expenseKind: t.expenseKind || "OPEX",
      };
    },
    kindLabel(v) {
      if (v === "CAPEX") return this.$t("comptabilite.expenseKindCapex");
      if (v === "OTHER") return this.$t("comptabilite.expenseKindOther");
      return this.$t("comptabilite.expenseKindOpex");
    },
    kindColor(v) {
      if (v === "CAPEX") return "purple";
      if (v === "OTHER") return "orange";
      return "blue";
    },
    async loadTypes() {
      try {
        const res = await apiClient.get("/compV3/depense/types");
        this.types = res.data || [];
      } catch (e) {
        this.types = [];
      }
    },
    async loadSubcategories() {
      try {
        const res = await apiClient.get("/compV3/depense/subcategories");
        this.subcategories = res.data || [];
      } catch (e) {
        this.subcategories = [];
      }
    },
    async load() {
      this.loading = true;
      try {
        const res = await apiClient.get("/compV3/depense/categories");
        this.rows = res.data || [];
      } catch (e) {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
    categoryHasSubsWithClass(categoryId) {
      if (!categoryId) return false;
      return (this.subcategories || []).some((s) => {
        const cid = s.category && s.category._id ? s.category._id : s.category;
        return String(cid) === String(categoryId) && s.accountingClass;
      });
    },
    openModal(record) {
      this.editingId = record ? record._id : null;
      this.hasSubsWithClass = this.categoryHasSubsWithClass(
        record ? record._id : null
      );
      this.modalVisible = true;
      this.$nextTick(() => {
        if (record) {
          const tid =
            record.depenseType && record.depenseType._id
              ? record.depenseType._id
              : record.depenseType;
          this.form.setFieldsValue({
            name: record.name,
            depenseType: tid,
            accountingClass: record.accountingClass || undefined,
          });
        } else {
          this.form.resetFields();
        }
      });
    },
    closeModal() {
      this.modalVisible = false;
      this.editingId = null;
      this.hasSubsWithClass = false;
    },
    onModalOk(e) {
      e.preventDefault();
      this.submit();
    },
    submit() {
      this.form.validateFields((err, values) => {
        if (err) return;
        const payloadData = {
          name: values.name,
          depenseType: values.depenseType,
          accountingClass: values.accountingClass || "",
        };
        this.saving = true;
        const payload = { data: payloadData };
        const req = this.editingId
          ? apiClient.patch(
              `/compV3/depense/categories/${this.editingId}`,
              payload
            )
          : apiClient.put("/compV3/depense/categories", payload);
        req
          .then((res) => {
            const cascade = res.data && res.data.cascade;
            if (
              cascade &&
              typeof cascade.subcategoriesDeactivated === "number" &&
              cascade.categoriesDeactivated === undefined
            ) {
              this.$message.success(
                this.$t("comptabilite.categoryDeactivatedCascade", {
                  subcategories: cascade.subcategoriesDeactivated,
                })
              );
            } else {
              this.$message.success(this.$t("success.operationEffectue"));
            }
            this.closeModal();
            this.loadSubcategories();
            this.load();
          })
          .catch((err) => showApiError(this, err))
          .finally(() => {
            this.saving = false;
          });
      });
    },
    remove(id) {
      apiClient
        .patch(`/compV3/depense/categories/${id}`, {
          data: { status: "inactive" },
        })
        .then((res) => {
          const cascade = res.data && res.data.cascade;
          if (
            cascade &&
            typeof cascade.subcategoriesDeactivated === "number" &&
            cascade.categoriesDeactivated === undefined
          ) {
            this.$message.success(
              this.$t("comptabilite.categoryDeactivatedCascade", {
                subcategories: cascade.subcategoriesDeactivated,
              })
            );
          } else {
            this.$message.success(this.$t("success.operationEffectue"));
          }
          this.load();
          this.loadTypes();
          this.loadSubcategories();
        })
        .catch((err) => showApiError(this, err));
    },
  },
};
</script>
<style scoped>
.compact-param-form >>> .ant-form-item {
  margin-bottom: 10px;
}
.text-warning {
  color: #fa8c16;
}
</style>
